import { Result, Risky } from '@rotomeca/rop';
import { EMPTY_STRING, isNullOrUndefined } from '@rotomeca/utils';
import BnumElementInternal from 'components/bnum-element-states';
import { Attr, Autobind, Data, Define, Fire } from 'core/decorators';
import { NonStd } from 'core/decorators/nonstd';
import { Schedule } from 'core/decorators/schedule';
import { UI } from 'core/decorators/ui';
import { UpdateAll } from 'core/decorators/updateAll';
import Log from 'core/utils/Log';
import { Nullable } from '@rotomeca/utils';
import { INPUT_BASE_STYLE } from '../input/bnum-input';
import style from './bnum-select.less';
import { TAG_SELECT } from 'core/utils/constants/tags';
import { HTMLBnumIcon } from '../icon/bnum-icon';

//#region Types
/**
 * Interface pour la récupération des données Ui de `HTMLBnumSelect`
 */
export interface BnumSelectUi {
	/**
	 * slot du label
	 */
	slotLabel: HTMLSlotElement;
	/**
	 * slot de l'indice
	 */
	slotHint: HTMLSlotElement;
	/**
	 * select interne
	 */
	select: HTMLSelectElement;
}

type Value = string | number | boolean;

/**
 * Item qui sert à la création d'une option pour {@link HTMLBnumSelect.addOption}
 */
export type BnumSelectAddOptionObject = {
	/**
	 * Valeur de l'option dans l'attribut `value`
	 */
	value: Nullable<Value>;
	/**
	 * Texte de l'option. Si {@link BnumSelectAddOptionObject.value} est null ou pas défini, ça sera la valeur de l'option.
	 */
	text: string;
	/**
	 * Si vrai, l'option sera ajouté avant les autres options.
	 */
	prepend?: boolean;
};

//#endregon Types

//#region Global Constants
const SYNCED_ATTRIBUTES = [
	'autocomplete',
	'autofocus',
	'disabled',
	'form',
	'multiple',
	'required',
	'size',
	'name',
	'value',
];

const TEMPLATE = (
	<div class="bnum-select__container">
		<label
			id="select-label"
			class="bnum-select__container__label label-container"
			for="select"
		>
			<span class="bnum-select__container__label--legend label-container--label">
				<slot name="label"></slot>
			</span>
			<span class="bnum-select__container__label--hint label-container--hint">
				<slot name="hint"></slot>
			</span>
		</label>
		<div class="select-container">
			<select
				id="select"
				class="bnum-select__container__select input-like"
			></select>
			<HTMLBnumIcon data-icon="keyboard_arrow_down" class="icon-arrow-down" />
		</div>
	</div>
);
//#endregion Global Constants

/**
 *
 * @category Input
 *
 * @structure Defaut
 * <bnum-select>
 *   <span slot="label">Un select</span>
 *   <option value="none" selected disabled>Choisissez une option</option>
 *   <option value="a">a</option>
 *   <option value="b">b</option>
 * </bnum-select>
 *
 * @structure Avec des optgroup
 * <bnum-select>
 *   <span slot="label">Un select</span>
 *   <optgroup label="yolo">
 *   <option value="none" selected disabled>Choisissez une option</option>
 *   <option value="a">a</option>
 *   <option value="b">b</option>
 *   </optgroup>
 *   <optgroup label="swag">
 *   <option value="c">c</option>
 *   <option value="d">d</option>
 *   <option value="e">e</option>
 *   </optgroup>
 * </bnum-select>
 *
 * @structure Avec des data
 * <bnum-select data-legend="Legende data" data-hint="Indice !" data-default-value="none" data-default-text="Choisissez un option">
 * <option value="a">a</option>
 * <option value="b">b</option>
 * </bnum-select>
 *
 * @structure Sans légendes
 * <bnum-select no-legend data-legend="Legende data" data-hint="Indice !" data-default-value="none" data-default-text="Choisissez un option">
 * <option value="a">a</option>
 * <option value="b">b</option>
 * </bnum-select>
 *
 * @slot label - Légende du select
 * @slot hint - Légende additionnel
 *
 * @attr {undefined | boolean} (optional) no-legend - Cache visuellement la légende. (Ne dispence pas d'en mettre une.)
 * @attr {undefined | string} (optional) name - Nom de l'élément
 * @attr {undefined | string} (optional) data-legend - Texte de la légende. Est écrasé si le slot est défini.
 * @attr {undefined | string} (optional) data-hint - Texte additionnel de la légende. Est écrasé si le slot est défini.
 * @attr {undefined | string} (optional) data-default-value - Génère une option par défaut avec cette valeur.
 * @attr {undefined | string} (optional) data-default-text - Génère une option par défaut avec ce texte.
 *
 * @event {CustomEvent<{innerEvent: Event, caller: HTMLBnumSelect}>} change - Lorsque le select change de valeur.
 */
@Define({
	tag: TAG_SELECT,
	template: TEMPLATE,
	styles: [INPUT_BASE_STYLE, style],
})
@UpdateAll()
export class HTMLBnumSelect extends BnumElementInternal {
	//#region Constants
	/**
	 * Required to participate in HTMLFormElement
	 */
	static readonly formAssociated = true;
	//#endregion Constants

	//#region Private fields
	#_observer: Nullable<MutationObserver> = null;
	#_initValue: Nullable<string> = null;
	//#endregion Private fields

	//#region Getters/Setters
	/**
	 * Elements d'interface interne
	 */
	@UI({
		slotLabel: '#select-label slot[name="label"]',
		slotHint: '#select-label slot[name="hint"]',
		select: '#select',
	})
	accessor #_ui!: BnumSelectUi;

	/**
	 * Récupère l'information du data-legend.
	 *
	 * @remark
	 * `data-legend` correspond à la légende affiché par défaut.
	 */
	@Data({ setter: false }) accessor #_legend: Nullable<string> = null;
	/**
	 * Récupère l'information du data-hint.
	 *
	 * @remark
	 * `data-hint` correspond à l'indice affiché par défaut.
	 */
	@Data({ setter: false }) accessor #_hint: Nullable<string> = null;
	/**
	 * Récupère l'information du data-default-value.
	 *
	 * @remark
	 * `data-default-value` génère un élément `<option value="${this.#_defaultValue}" selected disabled>${this.#_defaultText}</option>`
	 */
	@Data('default-value', { setter: false })
	accessor #_defaultValue: Nullable<string> = null;
	/**
	 * Récupère l'information du data-default-text.
	 *
	 * @remark
	 * `data-default-text` génère un élément `<option value="${this.#_defaultValue}" selected disabled>${this.#_defaultText}</option>`
	 */
	@Data('default-text', { setter: false })
	accessor #_defaultText: Nullable<string> = null;

	/**
	 * Nom de l'input
	 */
	@Attr() accessor name: string = EMPTY_STRING;
	/**
	 * Si l'attribut `no-legend` est actif, la légende ne s'affichera pas (seulement pour les lecteurs d'écrans).
	 */
	@Attr('no-legend') accessor noLegend: boolean = true;

	/**
	 * Valeur du select
	 */
	get value() {
		return this.#_ui.select.value;
	}
	set value(value: string) {
		this.#_setFormValue(value);
		this.#_ui.select.value = value;
	}

	/**
	 * Tout les options disponibles
	 */
	get options(): ReadonlyArray<HTMLOptionElement> {
		return Array.from((this.shadowRoot || this).querySelectorAll('option'));
	}

	/**
	 * Select dans le shadow-root
	 */
	get select(): HTMLSelectElement {
		return this.#_ui.select;
	}

	/**
	 * Récupère les options et optgroup du light-dom.
	 */
	get #_lightOptions() {
		return Array.from(
			this.querySelectorAll(':scope > option, :scope > optgroup'),
		);
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
		this.#_tryInitValue({ ignoreSelectedValue: true }).tapError(error => {
			Log.error(
				'HTMLBnumSelectElement',
				"Impossible d'initialiser la valeur par défaut !",
				error,
			);
		});
	}

	/**
	 * Callback appelée lorsque le composant est ajouté au DOM.
	 *
	 * Déclenche le rendu du composant.
	 *
	 * @override Pour pouvoir ajouter un observer et observer le light-dom.
	 */
	connectedCallback(): void {
		super.connectedCallback();
		(this.#_observer ??= new MutationObserver(this._obserse)).observe(this, {
			childList: true,
		});
	}

	/**
	 * On attache un shadow-dom custom pour pouvoir déléger le focus.
	 * @returns ShadowRoot ouvert avec le focus délégué.
	 */
	protected _p_attachCustomShadow(): Nullable<ShadowRoot> {
		return this.attachShadow({ mode: 'open', delegatesFocus: true });
	}

	/**
	 * @inheritdoc
	 */
	protected _p_preload(): void {
		this.#_setDefault();
	}

	/**
	 * @inheritdoc
	 */
	protected _p_buildDOM(): void {
		this.#_moveOptions();
	}

	/**
	 * @inheritdoc
	 */
	protected _p_attach(): void {
		this.#_tryInitValue().match({
			Ok: () => {
				this.#_setDataLegend().#_setDataHint().#_initListeners().#_sync();

				if (!this.#_hasLegend) {
					Log.warn('HTMLBnumSelect', 'Vous devez mettre un libellé !');
				}
			},
			Err: error => {
				Log.error(
					'HTMLBnumSelect',
					"Impossible d'initialiser la valeur du select !",
					error,
					this,
				);
			},
		});
	}

	/**
	 * @inheritdoc
	 */
	protected _p_detach(): void {
		this.#_observer?.disconnect?.();
	}

	/**
	 * @inheritdoc
	 */
	protected _p_update(): void | Nullable<'break'> {
		this.#_sync();
	}
	//#endregion Lifecycle

	//#region Publics Methods

	/**
	 * Ajoute un groupe d'option
	 * @param group Groupe à ajouter
	 * @returns Le groupe ajouté au shadow-dom
	 */
	addOptGroup(group: HTMLOptGroupElement): HTMLOptGroupElement {
		this.appendChild(group);
		return group;
	}

	/**
	 * Ajoute une option dans le select
	 * @param option Element html à ajouter
	 * @param param1 Paramètres optionnels
	 * @returns Chaîne
	 */
	addOption(
		option: HTMLOptionElement,
		{ prepend }?: { prepend?: boolean },
	): this;
	/**
	 * Ajoute une option dans le select à partir d'un objet
	 * @param item option à ajouter à partir d'un objet js.
	 * @returns Option créée
	 */
	addOption(item: BnumSelectAddOptionObject): HTMLOptionElement;
	/**
	 * Ajoute une option dans le select
	 * @param opt Option à ajouter
	 * @param param1
	 * @returns Chaîne ou option créée
	 */
	addOption(
		opt: HTMLOptionElement | BnumSelectAddOptionObject,
		{ prepend = false }: { prepend?: boolean } = {},
	): HTMLOptionElement | this {
		let returnElement: HTMLOptionElement | this;
		let option: HTMLOptionElement;

		if (opt instanceof HTMLOptionElement) {
			option = opt;
			returnElement = this;
		} else {
			option = document.createElement('option');

			if (opt.value !== null && opt.value !== undefined)
				option.value = String(opt.value);

			option.text = opt.text;
			prepend = prepend || opt.prepend || false;
			returnElement = option;
		}

		if (prepend) this.prepend(option);
		else this.appendChild(option);

		return returnElement;
	}

	// --- Formulaire --
	/**
	 * Réinitialise la valeur du champ lors d'une remise à zéro du formulaire parent.
	 */
	formResetCallback(): void {
		this.value = this.#_defaultValue ?? EMPTY_STRING;
	}

	/**
	 * Active ou désactive le champ selon l'état du fieldset parent.
	 */
	formDisabledCallback(disabled: boolean): void {
		if (disabled) this.setAttribute('disabled', 'disabled');
		this.#_sync();
	}
	//#endregion Publics Methods

	//#region Private methods
	#_initListeners() {
		return this.#_onInnerSelect();
	}

	@Autobind
	private _obserse(mutations: MutationRecord[]) {
		const hasOptionMutation = mutations.some(m =>
			Array.from(m.addedNodes).some(
				n => n instanceof HTMLOptionElement || n instanceof HTMLOptGroupElement,
			),
		);

		if (hasOptionMutation) {
			this.#_scheduleMoveOptions();
		}
	}

	@Schedule()
	#_scheduleMoveOptions() {
		this.#_moveOptions();
	}

	#_moveOptions() {
		this.#_ui.select.append(...this.#_lightOptions);
		return this;
	}

	#_setDataLegend() {
		return this.#_setLabelPart(this.#_legend, this.#_ui.slotLabel);
	}

	#_setDataHint() {
		return this.#_setLabelPart(this.#_hint, this.#_ui.slotHint);
	}

	#_setLabelPart(what: Nullable<string>, slot: HTMLSlotElement) {
		if (what) slot.appendChild(this._p_createTextNode(what));
		return this;
	}

	#_hasLegend() {
		const hasLabel =
			this.querySelectorAll('[slot="label"]').length > 0 || this.#_legend;
		const hasHint =
			this.querySelectorAll('[slot="hint"]').length > 0 || this.#_hint;
		return hasLabel || hasHint;
	}

	#_setDefault() {
		const value = this.#_defaultValue;
		const text = this.#_defaultText;

		if (value || text) {
			const effectiveValue = value || text;
			const effectiveText = text || value;
			const createdOption = this.addOption({
				value: effectiveValue,
				text: effectiveText!,
				prepend: true,
			});
			createdOption.setAttribute('selected', 'true');
			createdOption.setAttribute('disabled', 'disabled');
		}

		return this;
	}

	#_sync() {
		const select = this.#_ui.select;
		for (const attr of SYNCED_ATTRIBUTES) {
			if (this.hasAttribute(attr))
				select.setAttribute(attr, this.getAttribute(attr)!);
			else if (select.hasAttribute(attr)) select.removeAttribute(attr);
		}
	}

	@Risky()
	#_tryInitValue({
		ignoreSelectedValue = false,
	}: { ignoreSelectedValue?: boolean } = {}): Result<void> {
		if (isNullOrUndefined(this.#_initValue))
			this.#_initValue =
				this.#_defaultValue ?? (ignoreSelectedValue ? null : this.value);

		return Result.Ok();
	}

	@Risky()
	#_setFormValue(value: string): Result<void> {
		this._p_internal.setFormValue(value);
		return Result.Ok();
	}

	@Autobind
	@Fire('change')
	private _fireSelect(event: Event) {
		return { innerEvent: event, caller: this };
	}

	#_onInnerSelect() {
		this.#_ui.select.addEventListener('change', this._fireSelect);
		return this;
	}
	//#endregion Private methods

	//#region Static
	/**
	 * Méthode interne pour définir les attributs observés.
	 * @returns Attributs à observer
	 * @deprecated Utilisez le décorateur {@link Observe} du commit 3e38db0162eef596874dbe32490d9e96b09fb1c0
	 * @see [feat(composants): ✨ Ajout d'un décorateur pour réduire le boilerplate des attibuts à observer](https://github.com/messagerie-melanie2/design-system-bnum/commit/3e38db0162eef596874dbe32490d9e96b09fb1c0)
	 */
	@NonStd('Deprecated')
	protected static _p_observedAttributes(): string[] {
		return [...super._p_observedAttributes(), ...SYNCED_ATTRIBUTES];
	}

	static Create({
		label = null,
		options = [],
		defaults = null,
		hint = null,
	}: {
		label?: Nullable<string>;
		options?:
			| string[]
			| HTMLOptionElement[]
			| HTMLOptGroupElement[]
			| (string | HTMLOptionElement | HTMLOptGroupElement)[];
		defaults?: Nullable<{ text: string; value: Value }>;
		hint?: Nullable<string>;
	} = {}): HTMLBnumSelect {
		const node = document.createElement(this.TAG) as HTMLBnumSelect;

		if (!label) node.setAttribute('no-legend', 'true');
		else node.setAttribute('data-legend', label);

		if (hint) node.setAttribute('data-hint', hint);

		if (defaults && defaults.text && defaults.value) {
			node.setAttribute('data-default-text', defaults.text);
			node.setAttribute(
				'data-default-value',
				defaults.value as unknown as string,
			);
		}

		if (options && options.length > 0) {
			for (let element of options) {
				if (typeof element === 'string') {
					const doc = new DOMParser().parseFromString(element, 'text/html');
					element = doc.body.firstChild as unknown as HTMLOptGroupElement;
				}

				node.appendChild(element);
			}
		}

		return node;
	}
	//#endregion Static
}
